import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Header } from '../components/Header';
import { SkipLink } from '../components/SkipLink';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { QrCode, Download, Trash2, BarChart, Plus, Copy, Volume2, Webhook, Link2, ExternalLink, Brain, Target, Sparkles, BookOpen, AlertTriangle } from 'lucide-react';
import { WebhookSettings } from '../components/WebhookSettings';
import { FormPicker } from '../components/FormPicker';
import { LeadScoringEditor } from '../components/LeadScoringEditor';
import { PlaybookSelector } from '../components/PlaybookSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const AVAILABLE_VOICES = [
  { id: 'nova', label: 'Nova', desc: 'Warm & friendly', gender: 'female' },
  { id: 'shimmer', label: 'Shimmer', desc: 'Bright & energetic', gender: 'female' },
  { id: 'coral', label: 'Coral', desc: 'Natural & warm', gender: 'female' },
  { id: 'sage', label: 'Sage', desc: 'Smooth & measured', gender: 'female' },
  { id: 'alloy', label: 'Alloy', desc: 'Balanced & clear', gender: 'male' },
  { id: 'echo', label: 'Echo', desc: 'Deep & confident', gender: 'male' },
  { id: 'fable', label: 'Fable', desc: 'Expressive & soft', gender: 'male' },
  { id: 'onyx', label: 'Onyx', desc: 'Rich & authoritative', gender: 'male' },
  { id: 'ash', label: 'Ash', desc: 'Calm & steady', gender: 'male' },
];

export const QRManagement = () => {
  const { user, token } = useAuth();
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formUrl, setFormUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [size, setSize] = useState(10);
  const [creating, setCreating] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [customVariant, setCustomVariant] = useState('short');
  const [externalSubmit, setExternalSubmit] = useState(true);
  const [storeResponses, setStoreResponses] = useState(true);
  const [voiceDialogQR, setVoiceDialogQR] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState('nova');
  const [previewingVoice, setPreviewingVoice] = useState(null);
  const [savingVoice, setSavingVoice] = useState(false);
  const [voiceGender, setVoiceGender] = useState('all');
  const [webhookDialogQR, setWebhookDialogQR] = useState(null);
  const [slugDialogQR, setSlugDialogQR] = useState(null);
  const [slugValue, setSlugValue] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);
  const [shorteningQR, setShorteningQR] = useState(null);
  const [shortenerConfigs, setShortenerConfigs] = useState([]);
  const [scoringDialogQR, setScoringDialogQR] = useState(null);
  const [playbookDialogQR, setPlaybookDialogQR] = useState(null);
  const [formUsage, setFormUsage] = useState(null);
  const navigate = useNavigate();

  const fetchFormUsage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/billing/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFormUsage(res.data?.usage?.forms_created || null);
    } catch {}
  }, [token]);

  const fetchQRCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/qr-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrCodes(response.data);
    } catch (error) {
      toast.error('Failed to load QR codes');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchShortenerConfigs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shorteners/configs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShortenerConfigs(res.data);
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchQRCodes();
    fetchShortenerConfigs();
    fetchFormUsage();
  }, [fetchQRCodes, fetchShortenerConfigs, fetchFormUsage]);

  const openSlugDialog = (qr) => {
    setSlugDialogQR(qr);
    setSlugValue(qr.slug || '');
  };

  const handleSaveSlug = async () => {
    if (!slugDialogQR) return;
    setSavingSlug(true);
    try {
      if (slugValue.trim()) {
        await axios.patch(`${API}/qr-codes/${slugDialogQR.id}/slug`,
          { slug: slugValue.trim().toLowerCase() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Vanity URL saved!');
      } else {
        await axios.delete(`${API}/qr-codes/${slugDialogQR.id}/slug`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vanity URL removed');
      }
      setSlugDialogQR(null);
      fetchQRCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update slug');
    } finally {
      setSavingSlug(false);
    }
  };

  const handleShortenUrl = async (qrId) => {
    if (shortenerConfigs.length === 0) {
      toast.error('No URL shortener configured. Add one in Settings first.');
      return;
    }
    setShorteningQR(qrId);
    try {
      const res = await axios.post(`${API}/shorteners/shorten`,
        { qr_id: qrId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Shortened via ${res.data.provider}: ${res.data.short_url}`);
      navigator.clipboard.writeText(res.data.short_url);
      fetchQRCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to shorten URL');
    } finally {
      setShorteningQR(null);
    }
  };

  const handleCopyVanityUrl = (slug) => {
    const link = `${FRONTEND_URL}/f/${slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Vanity URL copied!');
  };


  const toggleInterviewMode = async (qr) => {
    const newMode = qr.interview_mode === 'adaptive' ? 'strict' : 'adaptive';
    try {
      await axios.patch(`${API}/qr-codes/${qr.id}/interview-mode`,
        { interview_mode: newMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Switched to ${newMode === 'adaptive' ? 'Adaptive AI' : 'Strict Form'} mode`);
      fetchQRCodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update mode');
    }
  };


  const handleCreateQR = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      await axios.post(
        `${API}/qr-codes`,
        { 
          form_url: formUrl, 
          title, 
          description,
          color,
          bg_color: bgColor,
          size,
          welcome_message: welcomeMessage || null,
          custom_variant: customVariant,
          external_submit_enabled: externalSubmit,
          store_responses: storeResponses
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('QR code created successfully!');
      setShowCreateDialog(false);
      setFormUrl('');
      setTitle('');
      setDescription('');
      setColor('#000000');
      setBgColor('#FFFFFF');
      setSize(10);
      setWelcomeMessage('');
      setCustomVariant('short');
      setExternalSubmit(true);
      setStoreResponses(true);
      fetchQRCodes();
      fetchFormUsage();
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      if (status === 429 && data?.error_code === 'LIMIT_REACHED') {
        toast.error(`Form limit reached (${data.current}/${data.limit}). Upgrade your plan.`);
      } else {
        toast.error(data?.detail || 'Failed to create QR code');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadQR = async (qrId, qrTitle) => {
    try {
      const response = await axios.get(`${API}/qr-codes/${qrId}/image`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${qrTitle.replace(/\s+/g, '-')}-QR.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('QR code downloaded!');
    } catch (error) {
      toast.error('Failed to download QR code');
    }
  };

  const handleCopyLink = (uniqueCode) => {
    const link = `${FRONTEND_URL}/fill/${uniqueCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard!');
  };

  const handleDeleteQR = async (qrId) => {
    if (!window.confirm('Are you sure you want to delete this QR code?')) return;

    try {
      await axios.delete(`${API}/qr-codes/${qrId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('QR code deleted');
      fetchQRCodes();
    } catch (error) {
      toast.error('Failed to delete QR code');
    }
  };

  const openVoiceDialog = (qr) => {
    setVoiceDialogQR(qr);
    setSelectedVoice(qr.voice_preference || 'nova');
  };

  const handleVoicePreview = async (voiceId) => {
    setPreviewingVoice(voiceId);
    try {
      const sampleText = 'Γεια σας! Θα σας καθοδηγήσω στη φόρμα, βήμα βήμα. Πώς σας φαίνεται αυτή η φωνή;';
      const res = await axios.post(`${API}/voice/synthesize`,
        { text: sampleText, voice: voiceId, language: 'el' },
        { responseType: 'blob', timeout: 15000 }
      );
      const audioUrl = URL.createObjectURL(res.data);
      const audio = new Audio(audioUrl);
      audio.onended = () => { URL.revokeObjectURL(audioUrl); setPreviewingVoice(null); };
      audio.onerror = () => { URL.revokeObjectURL(audioUrl); setPreviewingVoice(null); };
      audio.play().catch(() => setPreviewingVoice(null));
    } catch {
      toast.error('Preview failed');
      setPreviewingVoice(null);
    }
  };

  const handleSaveVoice = async () => {
    if (!voiceDialogQR) return;
    setSavingVoice(true);
    try {
      await axios.patch(`${API}/qr-codes/${voiceDialogQR.id}/voice`,
        { voice_preference: selectedVoice },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Voice updated!');
      setVoiceDialogQR(null);
      fetchQRCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update voice');
    } finally {
      setSavingVoice(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SkipLink />
        <Header />
        <main id="main-content" className="flex items-center justify-center min-h-screen">
          <div className="text-center" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d4ed8] mx-auto" aria-hidden="true"></div>
            <p className="mt-4 text-[#475569]">Loading QR codes...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SkipLink />
      <Header />
      
      <main id="main-content" className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-black mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Conversational Capture
              </h1>
              <p className="text-[#475569]">Create AI-powered interviews for your forms — accessible with a QR scan</p>
            </div>
            
            <div className="flex items-center gap-3">
              {formUsage && formUsage.limit !== -1 && (
                <span
                  className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                    formUsage.current >= formUsage.limit
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      : formUsage.current >= formUsage.limit * 0.8
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                      : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  }`}
                  data-testid="form-usage-badge"
                >
                  {formUsage.current} / {formUsage.limit} forms
                </span>
              )}
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button
                  data-testid="create-qr-button"
                  className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] btn-hover-lift focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Create new QR code"
                  disabled={formUsage && formUsage.limit !== -1 && formUsage.current >= formUsage.limit}
                >
                  <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                  {formUsage && formUsage.limit !== -1 && formUsage.current >= formUsage.limit ? 'Limit Reached' : 'Create QR Code'}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md" aria-describedby="create-qr-desc">
                <DialogHeader>
                  <DialogTitle>Create QR Code for Form</DialogTitle>
                </DialogHeader>
                <p id="create-qr-desc" className="sr-only">Fill in the form details to create a new QR code</p>
                <form onSubmit={handleCreateQR} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Form Title <span className="text-red-600">*</span></Label>
                    <Input
                      id="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                      placeholder="Customer Feedback Form"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="form-url">Form URL <span className="text-red-600">*</span></Label>

                    {/* Form Picker from connected accounts */}
                    <div className="my-3">
                      <FormPicker onSelectForm={(form) => {
                        setTitle(form.title);
                        setFormUrl(form.url);
                      }} />
                    </div>
                    <div className="relative my-3">
                      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E2E8F0]" /></div>
                      <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-[#475569]">or enter manually</span></div>
                    </div>

                    <Input
                      id="form-url"
                      type="url"
                      value={formUrl}
                      onChange={(e) => setFormUrl(e.target.value)}
                      required
                      placeholder="https://docs.google.com/forms/... or typeform.com/to/..."
                      className="mt-1"
                      aria-describedby="form-url-help"
                    />
                    <p id="form-url-help" className="text-xs text-[#475569] mt-1">
                      Supports Google Forms, Microsoft Forms, Typeform, and custom in-page (HTML) surveys
                    </p>
                  </div>

                  {/* Custom survey variant — shown for non-Google/MS/Typeform (in-page HTML) survey URLs */}
                  {formUrl && !/google\.com\/forms|forms\.gle|forms\.office\.com|forms\.microsoft\.com|typeform\.com\/to/i.test(formUrl) && (
                    <div data-testid="custom-variant-block">
                      <Label htmlFor="custom-variant">Survey version</Label>
                      <select
                        id="custom-variant"
                        value={customVariant}
                        onChange={(e) => setCustomVariant(e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-[#E2E8F0] rounded-lg"
                        data-testid="custom-variant-select"
                      >
                        <option value="short">Short version</option>
                        <option value="extended">Extended version</option>
                      </select>
                      <p className="text-xs text-[#475569] mt-1">
                        For custom in-page surveys that offer more than one length.
                      </p>
                    </div>
                  )}

                  {/* Submission & storage controls */}
                  <div className="space-y-3 rounded-lg border border-[#E2E8F0] p-3 bg-[#F8FAFC]">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="external-submit-toggle">
                      <input
                        type="checkbox"
                        checked={externalSubmit}
                        onChange={(e) => setExternalSubmit(e.target.checked)}
                        className="mt-1 h-4 w-4 accent-[#1d4ed8]"
                      />
                      <span className="text-sm text-[#0f172a]">
                        Send answers to the original form
                        <span className="block text-xs text-[#475569]">When on, completed answers are submitted back to the source form.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="store-responses-toggle">
                      <input
                        type="checkbox"
                        checked={storeResponses}
                        onChange={(e) => setStoreResponses(e.target.checked)}
                        className="mt-1 h-4 w-4 accent-[#1d4ed8]"
                      />
                      <span className="text-sm text-[#0f172a]">
                        Store answers in Evenly (for testing)
                        <span className="block text-xs text-[#475569]">Turn off to keep only counts — no answer content is saved on our side.</span>
                      </span>
                    </label>
                  </div>
                  <div>
                    <Label htmlFor="description">Description (Optional)</Label>
                    <Input
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Help us improve our service"
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="color">QR Color</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          id="color"
                          type="color"
                          value={color}
                          onChange={(e) => setColor(e.target.value)}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          type="text"
                          value={color}
                          onChange={(e) => setColor(e.target.value)}
                          placeholder="#000000"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="bg-color">Background</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          id="bg-color"
                          type="color"
                          value={bgColor}
                          onChange={(e) => setBgColor(e.target.value)}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          type="text"
                          value={bgColor}
                          onChange={(e) => setBgColor(e.target.value)}
                          placeholder="#FFFFFF"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="welcome-msg">Welcome Message (Optional)</Label>
                    <textarea
                      id="welcome-msg"
                      value={welcomeMessage}
                      onChange={(e) => setWelcomeMessage(e.target.value)}
                      placeholder="e.g. Welcome to Acme Corp! Let's fill out your form together."
                      className="w-full mt-1 px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm resize-none"
                      rows={2}
                      maxLength={300}
                      data-testid="welcome-message-input"
                    />
                    <p className="text-xs text-[#475569] mt-1">
                      Custom branded greeting shown to users ({welcomeMessage.length}/300)
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="size">QR Code Size</Label>
                    <select
                      id="size"
                      value={size}
                      onChange={(e) => setSize(Number(e.target.value))}
                      className="w-full mt-1 px-3 py-2 border border-[#E2E8F0] rounded-lg"
                    >
                      <option value={8}>Small (8)</option>
                      <option value={10}>Medium (10)</option>
                      <option value={12}>Large (12)</option>
                      <option value={15}>Extra Large (15)</option>
                    </select>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      disabled={creating}
                      className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]"
                    >
                      {creating ? 'Creating...' : 'Create'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateDialog(false)}
                      className="rounded-full"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Limit reached upgrade CTA */}
          {formUsage && formUsage.limit !== -1 && formUsage.current >= formUsage.limit && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3" data-testid="form-limit-banner">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Form limit reached ({formUsage.current}/{formUsage.limit})
                </p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  Upgrade your plan to create more forms.
                </p>
              </div>
              <Button
                size="sm"
                className="rounded-full bg-red-600 hover:bg-red-700 text-white text-xs shrink-0"
                onClick={() => navigate('/billing')}
                data-testid="form-limit-upgrade-btn"
              >
                Upgrade
              </Button>
            </div>
          )}

          {qrCodes.length === 0 ? (
            <Card className="p-12 text-center border border-[#E2E8F0]">
              <QrCode className="h-12 w-12 text-[#475569] mx-auto mb-4" aria-hidden="true" />
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                No QR Codes Yet
              </h2>
              <p className="text-[#475569] mb-6">
                Create your first QR code to make your forms accessible with zero-UI
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {qrCodes.map((qr, index) => (
                <motion.div
                  key={qr.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="p-6 border border-[#E2E8F0] hover:border-[#1d4ed8] transition-all">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {qr.title}
                        </h3>
                        {qr.description && (
                          <p className="text-sm text-[#475569] mb-2">{qr.description}</p>
                        )}
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-[#E2E8F0] mb-4 flex items-center justify-center">
                      <img
                        src={`${API}/qr-codes/${qr.id}/image?token=${token}`}
                        alt={`QR code for ${qr.title}`}
                        className="w-32 h-32"
                        style={{ imageRendering: 'pixelated' }}
                      />
                    </div>

                    <div className="flex items-center justify-between text-sm text-[#475569] mb-3">
                      <div className="flex items-center space-x-1">
                        <BarChart className="h-4 w-4" aria-hidden="true" />
                        <span>{qr.submissions_count} submissions</span>
                      </div>
                      <span className="text-xs">
                        {new Date(qr.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Interview Mode + Vanity URL / Short URL badges */}
                    <div className="space-y-1.5 mb-3">
                      <div
                        className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg cursor-pointer transition-colors ${
                          qr.interview_mode === 'adaptive'
                            ? 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                            : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'
                        }`}
                        onClick={() => toggleInterviewMode(qr)}
                        data-testid={`interview-mode-badge-${qr.id}`}
                        title={`Click to switch to ${qr.interview_mode === 'adaptive' ? 'Strict Form' : 'Adaptive AI'} mode`}
                      >
                        {qr.interview_mode === 'adaptive' ? (
                          <Brain className="h-3.5 w-3.5 shrink-0" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5 shrink-0" />
                        )}
                        <span className="font-medium">
                          {qr.interview_mode === 'adaptive' ? 'Adaptive AI Interview' : 'Strict Form Mode'}
                        </span>
                      </div>
                      {qr.playbook_id && (
                        <div
                          className="flex items-center gap-1.5 text-xs bg-indigo-50 text-indigo-700 px-2.5 py-1.5 rounded-lg"
                          data-testid={`playbook-badge-${qr.id}`}
                        >
                          <BookOpen className="h-3.5 w-3.5 shrink-0" />
                          <span className="font-medium truncate">
                            {qr.playbook_id === 'pb-sales-discovery' ? 'Sales Discovery' :
                             qr.playbook_id === 'pb-customer-feedback' ? 'Customer Feedback' :
                             qr.playbook_id === 'pb-job-screening' ? 'Job Screening' :
                             'Custom Playbook'}
                          </span>
                        </div>
                      )}
                      {qr.slug && (
                        <div
                          className="flex items-center gap-1.5 text-xs bg-[#EFF6FF] text-[#1d4ed8] px-2.5 py-1.5 rounded-lg cursor-pointer hover:bg-[#DBEAFE] transition-colors"
                          onClick={() => handleCopyVanityUrl(qr.slug)}
                          data-testid={`vanity-url-badge-${qr.id}`}
                          title="Click to copy vanity URL"
                        >
                          <Link2 className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate font-medium">/f/{qr.slug}</span>
                          <Copy className="h-3 w-3 shrink-0 ml-auto opacity-60" />
                        </div>
                      )}
                      {qr.short_url && (
                        <div
                          className="flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-700 px-2.5 py-1.5 rounded-lg cursor-pointer hover:bg-emerald-100 transition-colors"
                          onClick={() => { navigator.clipboard.writeText(qr.short_url); toast.success('Short URL copied!'); }}
                          data-testid={`short-url-badge-${qr.id}`}
                          title="Click to copy short URL"
                        >
                          <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate font-medium">{qr.short_url}</span>
                          <Copy className="h-3 w-3 shrink-0 ml-auto opacity-60" />
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/qr-codes/${qr.id}/analytics`)}
                        className="flex-1 rounded-full"
                        aria-label={`View analytics for ${qr.title}`}
                      >
                        <BarChart className="h-4 w-4 mr-1" aria-hidden="true" />
                        Analytics
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setScoringDialogQR(qr)}
                        className="rounded-full"
                        aria-label={`Lead scoring for ${qr.title}`}
                        data-testid={`lead-scoring-btn-${qr.id}`}
                      >
                        <Target className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPlaybookDialogQR(qr)}
                        className="rounded-full"
                        aria-label={`Select playbook for ${qr.title}`}
                        data-testid={`playbook-btn-${qr.id}`}
                      >
                        <BookOpen className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openSlugDialog(qr)}
                        className="rounded-full"
                        aria-label={`Vanity URL settings for ${qr.title}`}
                        data-testid={`slug-settings-btn-${qr.id}`}
                      >
                        <Link2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleShortenUrl(qr.id)}
                        disabled={shorteningQR === qr.id}
                        className="rounded-full"
                        aria-label={`Shorten URL for ${qr.title}`}
                        data-testid={`shorten-url-btn-${qr.id}`}
                      >
                        <ExternalLink className={`h-4 w-4 ${shorteningQR === qr.id ? 'animate-spin' : ''}`} aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openVoiceDialog(qr)}
                        className="rounded-full"
                        aria-label={`Change voice for ${qr.title}`}
                        data-testid={`voice-settings-btn-${qr.id}`}
                      >
                        <Volume2 className="h-4 w-4 mr-1" aria-hidden="true" />
                        Voice
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setWebhookDialogQR(qr)}
                        className="rounded-full"
                        aria-label={`Webhook settings for ${qr.title}`}
                        data-testid={`webhook-settings-btn-${qr.id}`}
                      >
                        <Webhook className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadQR(qr.id, qr.title)}
                        className="rounded-full"
                        aria-label={`Download QR code for ${qr.title}`}
                      >
                        <Download className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCopyLink(qr.unique_code)}
                        className="rounded-full"
                        aria-label={`Copy link for ${qr.title}`}
                      >
                        <Copy className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteQR(qr.id)}
                        className="rounded-full text-red-600 hover:bg-red-50"
                        aria-label={`Delete QR code for ${qr.title}`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </div>
                    {qr.voice_preference && qr.voice_preference !== 'nova' && (
                      <p className="text-xs text-[#475569] mt-2 flex items-center gap-1">
                        <Volume2 className="h-3 w-3" aria-hidden="true" />
                        Voice: {AVAILABLE_VOICES.find(v => v.id === qr.voice_preference)?.label || qr.voice_preference}
                      </p>
                    )}
                    {qr.welcome_message && (
                      <p className="text-xs text-[#475569] mt-1 italic truncate" data-testid={`welcome-msg-preview-${qr.id}`}>
                        "{qr.welcome_message}"
                      </p>
                    )}
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Voice Selection Dialog */}
      <Dialog open={!!voiceDialogQR} onOpenChange={(open) => { if (!open) setVoiceDialogQR(null); }}>
        <DialogContent className="sm:max-w-lg" aria-describedby="voice-dialog-desc">
          <DialogHeader>
            <DialogTitle data-testid="voice-dialog-title">
              Change Voice — {voiceDialogQR?.title}
            </DialogTitle>
          </DialogHeader>
          <p id="voice-dialog-desc" className="text-sm text-[#475569] -mt-2 mb-3">
            Pick the AI voice for your form. Filter by male/female and tap the speaker to hear a sample before saving.
          </p>

          {/* Gender filter */}
          <div className="flex items-center gap-2 mb-3" data-testid="voice-gender-filter">
            {[
              { id: 'all', label: 'All' },
              { id: 'female', label: 'Female' },
              { id: 'male', label: 'Male' },
            ].map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setVoiceGender(g.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  voiceGender === g.id
                    ? 'bg-[#1d4ed8] text-white'
                    : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'
                }`}
                data-testid={`voice-gender-${g.id}`}
              >
                {g.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[340px] overflow-y-auto pr-1">
            {AVAILABLE_VOICES.filter(v => voiceGender === 'all' || v.gender === voiceGender).map((v) => (
              <div
                key={v.id}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                  selectedVoice === v.id
                    ? 'border-[#1d4ed8] bg-[#EFF6FF]'
                    : 'border-[#E2E8F0] hover:border-[#94A3B8]'
                }`}
                onClick={() => setSelectedVoice(v.id)}
                role="radio"
                aria-checked={selectedVoice === v.id}
                tabIndex={0}
                data-testid={`voice-option-${v.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm">{v.label}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      v.gender === 'female' ? 'bg-pink-50 text-pink-600' : 'bg-blue-50 text-blue-600'
                    }`}>
                      {v.gender === 'female' ? 'Female' : 'Male'}
                    </span>
                  </div>
                  <p className="text-xs text-[#475569]">{v.desc}</p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={previewingVoice !== null}
                  onClick={(e) => { e.stopPropagation(); handleVoicePreview(v.id); }}
                  className={`rounded-full shrink-0 ${
                    previewingVoice === v.id ? 'text-[#1d4ed8] animate-pulse' : 'text-[#475569] hover:text-[#1d4ed8]'
                  }`}
                  aria-label={`Preview ${v.label} voice`}
                  data-testid={`voice-preview-${v.id}`}
                >
                  <Volume2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-3 border-t border-[#E2E8F0] mt-2">
            <Button
              onClick={handleSaveVoice}
              disabled={savingVoice || selectedVoice === (voiceDialogQR?.voice_preference || 'nova')}
              className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] flex-1"
              data-testid="voice-save-button"
            >
              {savingVoice ? 'Saving...' : 'Save Voice'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setVoiceDialogQR(null)}
              className="rounded-full"
              data-testid="voice-cancel-button"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Webhook Settings Dialog */}
      <Dialog open={!!webhookDialogQR} onOpenChange={(open) => { if (!open) setWebhookDialogQR(null); }}>
        <DialogContent className="sm:max-w-lg" aria-describedby="webhook-dialog-desc">
          <DialogHeader>
            <DialogTitle data-testid="webhook-dialog-title">
              Webhook — {webhookDialogQR?.title}
            </DialogTitle>
          </DialogHeader>
          <p id="webhook-dialog-desc" className="sr-only">Configure webhook URL for this QR code</p>
          {webhookDialogQR && (
            <WebhookSettings qrId={webhookDialogQR.id} onClose={() => setWebhookDialogQR(null)} />
          )}
        </DialogContent>
      </Dialog>

      {/* Vanity URL (Slug) Dialog */}
      <Dialog open={!!slugDialogQR} onOpenChange={(open) => { if (!open) setSlugDialogQR(null); }}>
        <DialogContent className="sm:max-w-md" aria-describedby="slug-dialog-desc">
          <DialogHeader>
            <DialogTitle data-testid="slug-dialog-title">
              Vanity URL — {slugDialogQR?.title}
            </DialogTitle>
          </DialogHeader>
          <p id="slug-dialog-desc" className="text-sm text-[#475569] -mt-2 mb-3">
            Create a memorable, branded link for your form.
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="slug-input">Custom Slug</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-[#475569] whitespace-nowrap">{FRONTEND_URL}/f/</span>
                <Input
                  id="slug-input"
                  value={slugValue}
                  onChange={(e) => setSlugValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="my-feedback-form"
                  className="flex-1"
                  data-testid="slug-input"
                />
              </div>
              <p className="text-xs text-[#475569] mt-1.5">
                3-64 characters. Lowercase letters, numbers, and hyphens only.
              </p>
            </div>

            {slugValue && (
              <div className="p-3 rounded-lg bg-[#EFF6FF] border border-[#BFDBFE]">
                <p className="text-xs text-[#475569] mb-1">Preview:</p>
                <p className="text-sm font-medium text-[#1d4ed8] break-all" data-testid="slug-preview">
                  {FRONTEND_URL}/f/{slugValue}
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleSaveSlug}
                disabled={savingSlug}
                className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] flex-1"
                data-testid="slug-save-button"
              >
                {savingSlug ? 'Saving...' : slugValue ? 'Save Vanity URL' : 'Remove Vanity URL'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setSlugDialogQR(null)}
                className="rounded-full"
                data-testid="slug-cancel-button"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Scoring Dialog */}
      <Dialog open={!!scoringDialogQR} onOpenChange={(open) => { if (!open) setScoringDialogQR(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby="scoring-dialog-desc">
          <DialogHeader>
            <DialogTitle data-testid="scoring-dialog-title">
              Lead Scoring — {scoringDialogQR?.title}
            </DialogTitle>
          </DialogHeader>
          <p id="scoring-dialog-desc" className="sr-only">Configure lead scoring rules for this form</p>
          {scoringDialogQR && (
            <LeadScoringEditor
              qrId={scoringDialogQR.id}
              onClose={() => { setScoringDialogQR(null); fetchQRCodes(); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Playbook Dialog */}
      <Dialog open={!!playbookDialogQR} onOpenChange={(open) => { if (!open) setPlaybookDialogQR(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" aria-describedby="playbook-dialog-desc">
          <DialogHeader>
            <DialogTitle data-testid="playbook-dialog-title">
              AI Playbook — {playbookDialogQR?.title}
            </DialogTitle>
          </DialogHeader>
          <p id="playbook-dialog-desc" className="sr-only">Select an AI interview playbook for this form</p>
          {playbookDialogQR && (
            <PlaybookSelector
              qrId={playbookDialogQR.id}
              currentPlaybookId={playbookDialogQR.playbook_id}
              onUpdate={() => { setPlaybookDialogQR(null); fetchQRCodes(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
